<template>
  <div class="tab-content order-management-page">
    <div class="main-header">
      <div class="page-heading">
        <h1>{{ $t('controlCenter.orders.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.orders.subtitle') }}</p>
      </div>
    </div>

    <div class="card order-card">
      <div class="card-header filter-row order-filter-row">
        <el-select v-model="activeBrand" class="filter-select" @change="handleBrandChange">
          <el-option v-for="item in brandOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
        <el-input
          v-model.trim="filters.keyword"
          :placeholder="$t('controlCenter.orders.keywordPlaceholder')"
          clearable
          class="keyword-input"
          @keyup.enter.native="fetchOrders"
        ></el-input>
        <el-select v-model="filters.status" clearable class="filter-select">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option v-for="item in statusOptions" :key="item" :label="statusLabel(item)" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.planId" clearable class="filter-select">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option
            v-for="plan in planOptions"
            :key="plan.value"
            :label="plan.label"
            :value="plan.value"
          ></el-option>
        </el-select>
        <el-select v-model="filters.paymentMethod" clearable class="filter-select">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option
            v-for="method in paymentMethodOptions"
            :key="method"
            :label="method.toUpperCase()"
            :value="method"
          ></el-option>
        </el-select>
        <button class="btn btn-primary search-btn" @click="fetchOrders">
          <i class="fas fa-magnifying-glass"></i>
          {{ $t('controlCenter.common.search') }}
        </button>
      </div>

      <div class="card-body order-table-body">
        <el-table
          :data="orders"
          v-loading="loading"
          style="width: 100%"
          row-key="orderNo"
          :tree-props="{ children: 'children' }"
          default-expand-all
        >
          <el-table-column prop="orderNo" :label="$t('controlCenter.orders.columns.orderNo')" min-width="190">
            <template slot-scope="scope">
              <span class="order-no-cell">{{ scope.row.orderNo }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="companyName" :label="$t('controlCenter.orders.columns.company')" min-width="190">
            <template slot-scope="scope">
              <span class="company-cell">{{ scope.row.companyName }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.plan')" min-width="160">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.orderType === 'STORAGE'" class="storage-tag" size="small" type="info">
                {{ $t('controlCenter.orders.storageTag') }}
              </el-tag>
              <template v-else>{{ planLabel(scope.row.planId) }}</template>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.billingCycle')" min-width="120">
            <template slot-scope="scope">
              {{ billingCycleLabel(scope.row.billingCycle) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.amount')" min-width="150">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.finalAmount, scope.row.displayCurrency) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.status')" min-width="120">
            <template slot-scope="scope">
              <el-tag class="status-pill" size="small" :type="statusTagType(scope.row.status)">
                {{ statusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="paymentMethod" :label="$t('controlCenter.orders.columns.paymentMethod')" min-width="120"></el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.createdAt')" min-width="180">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.actions')" min-width="360">
            <template slot-scope="scope">
              <div class="action-buttons">
                <button class="btn btn-sm action-view" @click="openDetail(scope.row.orderNo)">
                  {{ $t('controlCenter.common.viewDetails') }}
                </button>
                <!-- <el-upload
                  class="invoice-upload"
                  action="/api/v1/oss/files"
                  :show-file-list="false"
                  :http-request="options => uploadInvoice(scope.row, options)"
                  :before-upload="beforeInvoiceUpload"
                >
                  <button class="btn btn-sm" :disabled="uploadingOrderNo === scope.row.orderNo">
                    {{ uploadingOrderNo === scope.row.orderNo ? $t('controlCenter.orders.invoiceUploading') : $t('controlCenter.orders.uploadInvoice') }}
                  </button> -->
                <!-- </el-upload> -->
                <button
                  class="btn btn-sm action-invoice"
                  :disabled="!canViewInvoice(scope.row)"
                  @click="viewInvoice(scope.row)"
                >
                  {{ $t('controlCenter.orders.viewInvoice') }}
                </button>
                <button class="btn btn-sm action-sync" @click="syncOrder(scope.row.orderNo)">
                  {{ $t('controlCenter.orders.syncStatus') }}
                </button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="pageNumber + 1"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="totalElements"
          layout="total, sizes, prev, pager, next"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        ></el-pagination>
      </div>
    </div>

    <el-dialog
      :title="$t('controlCenter.orders.detailTitle')"
      :visible.sync="detailDialogVisible"
      width="860px"
    >
      <div v-loading="detailLoading">
        <div v-if="selectedOrder" class="detail-layout">
          <div class="detail-grid">
            <div class="detail-item">
              <span>{{ $t('controlCenter.orders.columns.orderNo') }}</span>
              <strong>{{ selectedOrder.orderNo }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.common.status') }}</span>
              <strong>{{ statusLabel(selectedOrder.status) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.orders.columns.company') }}</span>
              <strong>{{ selectedOrder.companyName || '-' }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.orders.columns.plan') }}</span>
              <strong>{{ planLabel(selectedOrder.planId) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.orders.columns.billingCycle') }}</span>
              <strong>{{ billingCycleLabel(selectedOrder.billingCycle) }}</strong>
            </div>
            <div class="detail-item">
              <span>{{ $t('controlCenter.orders.columns.amount') }}</span>
              <strong>{{ formatAmount(selectedOrder.finalAmount, selectedOrder.displayCurrency) }}</strong>
            </div>
          </div>

          <div class="card detail-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('controlCenter.orders.invoiceItems') }}</h3>
            </div>
            <div class="card-body">
              <div v-for="item in selectedOrder.invoiceItems || []" :key="item.matter" class="invoice-row">
                <span>{{ item.matter }}</span>
                <strong>{{ formatAmount(item.amount, selectedOrder.displayCurrency) }}</strong>
              </div>
            </div>
          </div>

          <div class="card detail-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('controlCenter.orders.companyInfo') }}</h3>
            </div>
            <div class="card-body company-grid">
              <div class="detail-item">
                <span>{{ $t('controlCenter.orders.contactName') }}</span>
                <strong>{{ selectedOrder.companyContactName || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.orders.email') }}</span>
                <strong>{{ selectedOrder.companyEmail || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.orders.phone') }}</span>
                <strong>{{ selectedOrder.companyPhone || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.orders.registrationNumber') }}</span>
                <strong>{{ selectedOrder.companyRegistrationNumber || '-' }}</strong>
              </div>
              <div class="detail-item detail-span-2">
                <span>{{ $t('controlCenter.orders.address') }}</span>
                <strong>{{ selectedOrder.companyAddress || '-' }}</strong>
              </div>
            </div>
          </div>

          <div class="card detail-card">
            <div class="card-header">
              <h3 class="card-title">{{ $t('controlCenter.orders.defaultInvoiceInfo') }}</h3>
            </div>
            <div v-if="defaultInvoiceInfo" class="card-body company-grid">
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.title') }}</span>
                <strong>{{ defaultInvoiceInfo.title || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.invoiceType') }}</span>
                <strong>{{ defaultInvoiceInfo.invoiceType || 'VAT普通发票' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.taxId') }}</span>
                <strong>{{ defaultInvoiceInfo.taxId || '-' }}</strong>
              </div>
              <div class="detail-item detail-span-2">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.address') }}</span>
                <strong>{{ defaultInvoiceInfo.address || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.phone') }}</span>
                <strong>{{ defaultInvoiceInfo.phone || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankName') }}</span>
                <strong>{{ defaultInvoiceInfo.bankName || '-' }}</strong>
              </div>
              <div class="detail-item">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAccount') }}</span>
                <strong>{{ defaultInvoiceInfo.bankAccount || '-' }}</strong>
              </div>
              <div v-if="defaultInvoiceInfo.bankAddresses && defaultInvoiceInfo.bankAddresses.length" class="detail-item detail-span-2">
                <span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAddresses') }}</span>
                <strong>{{ defaultInvoiceInfo.bankAddresses.join('；') }}</strong>
              </div>
            </div>
            <div v-else class="card-body invoice-info-empty">
              {{ $t('controlCenter.orders.noDefaultInvoiceInfo') }}
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer-actions">
        <el-button @click="detailDialogVisible = false">{{ $t('controlCenter.common.close') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'
import { accountApi } from '@/api/account'
import brand from '@/branding'
import request from '@/utils/http'
import { getUserInfo } from '@/utils/auth'
import { formatPrice } from '@/utils/currency'

export default {
  name: 'OrderManagement',
  data() {
    return {
      loading: false,
      detailLoading: false,
      detailDialogVisible: false,
      orders: [],
      selectedOrder: null,
      defaultInvoiceInfo: null,
      uploadingOrderNo: '',
      totalElements: 0,
      pageNumber: 0,
      pageSize: 10,
      activeBrand: brand.id,
      brandOptions: [
        { value: 'lpt', label: 'LPT' },
        { value: 'mt', label: 'MindseT' }
      ],
      filters: {
        keyword: '',
        status: '',
        planId: '',
        paymentMethod: ''
      },
      planOptions: [],
      paymentMethodOptions: ['card', 'visa', 'mastercard', 'alipay', 'wechat', 'wechat_pay'],
      statusOptions: ['PENDING', 'SUCCESS', 'ACTIVE', 'FAILED', 'CANCELED', 'PAST_DUE']
    }
  },
  created() {
    this.fetchPurchaseConfig()
    this.fetchOrders()
  },
  methods: {
    async fetchPurchaseConfig() {
      try {
        const response = await paymentAdminApi.getPurchaseConfig(this.activeBrand)
        const data = response && response.data ? response.data : response
        this.planOptions = Object.keys(data.plans || {}).map(key => ({
          value: key,
          label: data.plans[key].name
        }))
        this.paymentMethodOptions = Array.from(new Set([
          ...(data.paymentMethods || []),
          ...this.paymentMethodOptions
        ]))
      } catch (error) {
        console.error('Failed to fetch purchase config:', error)
      }
    },
    async fetchOrders() {
      this.loading = true
      try {
        const response = await paymentAdminApi.getOrders({
          brandCode: this.activeBrand,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined,
          planId: this.filters.planId || undefined,
          paymentMethod: this.filters.paymentMethod || undefined,
          page: this.pageNumber,
          size: this.pageSize
        })
        const data = response && response.data ? response.data : response
        this.orders = data.content || []
        this.totalElements = data.totalElements || 0
        this.pageNumber = data.pageNumber || 0
        this.pageSize = data.pageSize || this.pageSize
      } catch (error) {
        console.error('Failed to fetch orders:', error)
        this.$message.error(this.$t('controlCenter.orders.loadError'))
      } finally {
        this.loading = false
      }
    },
    async openDetail(orderNo) {
      this.detailDialogVisible = true
      this.detailLoading = true
      this.defaultInvoiceInfo = null
      try {
        const response = await paymentAdminApi.getOrder(orderNo, this.activeBrand)
        this.selectedOrder = response && response.data ? response.data : response
        const snapshot = this.selectedOrder.invoiceInfoSnapshot
        if (snapshot && typeof snapshot === 'object' && Object.keys(snapshot).length > 0 && snapshot.title) {
          this.defaultInvoiceInfo = snapshot
        } else {
          try {
            const invoiceResponse = await accountApi.getInvoiceInfosForOrder(this.selectedOrder.orderNo || orderNo)
            const invoiceInfos = invoiceResponse && invoiceResponse.data ? invoiceResponse.data : invoiceResponse
            const infos = invoiceInfos || []
            this.defaultInvoiceInfo = infos.find(item => item.defaultInfo) || infos[0] || null
          } catch (invoiceError) {
            console.error('Failed to load default invoice information:', invoiceError)
          }
        }
      } catch (error) {
        console.error('Failed to fetch order detail:', error)
        this.$message.error(this.$t('controlCenter.orders.detailError'))
      } finally {
        this.detailLoading = false
      }
    },
    async syncOrder(orderNo) {
      try {
        await paymentAdminApi.syncOrderStatus(orderNo, this.activeBrand)
        this.$message.success(this.$t('controlCenter.orders.syncSuccess'))
        if (this.selectedOrder && this.selectedOrder.orderNo === orderNo) {
          await this.openDetail(orderNo)
        }
        await this.fetchOrders()
      } catch (error) {
        console.error('Failed to sync order status:', error)
        this.$message.error(this.$t('controlCenter.orders.syncError'))
      }
    },
    handleBrandChange() {
      this.pageNumber = 0
      this.detailDialogVisible = false
      this.selectedOrder = null
      this.fetchPurchaseConfig()
      this.fetchOrders()
    },
    beforeInvoiceUpload(file) {
      const acceptedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ]
      const isAccepted = acceptedTypes.includes(file.type)
      const isUnderLimit = file.size / 1024 / 1024 <= 10
      if (!isAccepted) {
        this.$message.error(this.$t('controlCenter.orders.invoiceTypeError'))
        return false
      }
      if (!isUnderLimit) {
        this.$message.error(this.$t('controlCenter.orders.invoiceSizeError'))
        return false
      }
      return true
    },
    async uploadInvoice(row, options) {
      this.uploadingOrderNo = row.orderNo
      try {
        const userInfo = getUserInfo() || {}
        const accountId = userInfo.accountId || userInfo.sub || userInfo.userId
        if (!accountId) {
          throw new Error('Missing accountId')
        }
        const formData = new FormData()
        formData.append('accountId', accountId)
        if (userInfo.userId || userInfo.sub) {
          formData.append('uploaderId', userInfo.userId || userInfo.sub)
        }
        formData.append('systemId', 'payment-order')
        formData.append('role', 'invoice')
        formData.append('files', options.file)
        const uploaded = await request.post('/api/v1/oss/files', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const file = Array.isArray(uploaded) ? uploaded[0] : uploaded
        if (!file || !file.id || !file.url) {
          throw new Error('Invalid upload response')
        }
        const invoiceResponse = await accountApi.getInvoiceInfosForOrder(row.orderNo)
        const invoiceInfos = invoiceResponse && invoiceResponse.data ? invoiceResponse.data : invoiceResponse
        const invoiceInfoSnapshot = (invoiceInfos || []).find(item => item.defaultInfo) || (invoiceInfos || [])[0] || null
        const updatedOrder = await paymentAdminApi.updateOrderInvoice(row.orderNo, {
          fileId: file.id,
          fileName: file.filename || options.file.name,
          fileUrl: file.url,
          invoiceInfoSnapshot
        })
        this.$message.success(this.$t('controlCenter.orders.invoiceUploadSuccess'))
        this.patchOrderInvoice(row.orderNo, updatedOrder)
        if (this.selectedOrder && this.selectedOrder.orderNo === row.orderNo) {
          this.selectedOrder = updatedOrder
        }
        if (options.onSuccess) options.onSuccess(file)
      } catch (error) {
        console.error('Failed to upload invoice:', error)
        this.$message.error(this.$t('controlCenter.orders.invoiceUploadError'))
        if (options.onError) options.onError(error)
      } finally {
        this.uploadingOrderNo = ''
      }
    },
    patchOrderInvoice(orderNo, updatedOrder) {
      const index = this.orders.findIndex(item => item.orderNo === orderNo)
      if (index === -1 || !updatedOrder) return
      this.$set(this.orders, index, {
        ...this.orders[index],
        invoiceFileId: updatedOrder.invoiceFileId,
        invoiceFileName: updatedOrder.invoiceFileName,
        invoiceFileUrl: updatedOrder.invoiceFileUrl
      })
    },
    canViewInvoice(row) {
      return !!(row && row.orderNo)
    },
    viewInvoice(row) {
      this.$router.push({
        path: '/buy/invoice',
        query: { orderNo: row.orderNo }
      })
    },
    handlePageChange(page) {
      this.pageNumber = page - 1
      this.fetchOrders()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNumber = 0
      this.fetchOrders()
    },
    planLabel(planId) {
      const plan = this.planOptions.find(item => item.value === planId)
      return plan ? plan.label : planId || '-'
    },
    billingCycleLabel(value) {
      const key = `controlCenter.billingCycleLabels.${value || 'one_year'}`
      const text = this.$t(key)
      return text === key ? value || '-' : text
    },
    statusLabel(status) {
      const key = `controlCenter.statusLabels.${status || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? status || this.$t('controlCenter.common.unknown') : text
    },
    statusTagType(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'SUCCESS' || normalized === 'ACTIVE') return 'success'
      if (normalized === 'PENDING') return 'warning'
      return 'danger'
    },
    formatAmount(amount, currency = 'RMB') {
      return `${currency} ${formatPrice(Number(amount || 0) / 100)}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return value.replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.order-management-page {
  --order-accent: #2563eb;
  --order-border: #e2e8f0;
  --order-muted: #64748b;
  --order-surface: #ffffff;
}

.order-management-page .main-header {
  margin-bottom: 22px;
}

.page-heading {
  min-width: 0;
}

.order-management-page .subtitle {
  max-width: 760px;
}

.order-card {
  overflow: hidden;
  border-color: var(--order-border);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.filter-row {
  gap: 12px;
  flex-wrap: wrap;
}

.order-filter-row {
  justify-content: flex-start !important;
  align-items: center;
  background: #f8fafc;
}

.keyword-input {
  width: 260px;
}

.filter-select {
  width: 180px;
}

.keyword-input {
  flex: 1 1 280px;
  max-width: 360px;
}

.filter-select {
  flex: 0 0 180px;
}

.search-btn {
  min-width: 104px;
  justify-content: center;
  white-space: nowrap;
}

.order-table-body {
  padding: 0 !important;
}

.order-no-cell,
.company-cell {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.order-no-cell {
  color: #334155;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
}

.company-cell {
  color: #0f172a;
  font-weight: 600;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 24px;
  background: var(--order-surface);
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.action-buttons .btn {
  min-width: 82px;
  justify-content: center;
  white-space: nowrap;
}

.invoice-upload {
  display: inline-flex;
}

.invoice-upload .btn {
  width: 100%;
}

.detail-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-grid,
.company-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.detail-card {
  margin-bottom: 0;
  box-shadow: none;
  border-color: var(--order-border);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;

  span {
    color: #64748b;
    font-size: 12px;
  }

  strong {
    color: #0f172a;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
}

.detail-span-2 {
  grid-column: span 2;
}

.invoice-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.invoice-row:last-child {
  border-bottom: none;
}

.invoice-info-empty {
  color: var(--order-muted);
  font-size: 13px;
}

.dialog-footer-actions {
  display: inline-flex;
  justify-content: flex-end;
}

@media screen and (max-width: 768px) {
  .keyword-input,
  .filter-select {
    width: 100%;
    max-width: 100%;
    flex-basis: auto;
  }

  .search-btn {
    width: 100%;
  }

  .pagination-wrap {
    justify-content: center;
    padding: 0 12px 18px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .btn,
  .action-buttons .el-button,
  .action-buttons .invoice-upload,
  .action-buttons .invoice-upload > div {
    width: 100%;
    margin-left: 0;
  }

  .action-buttons .invoice-upload .btn,
  .action-buttons .invoice-upload .el-button {
    width: 100%;
  }

  .order-table-body {
    margin: 0 -1px;
  }

  .order-table-body .el-table {
    min-width: 1060px;
  }

  .detail-grid,
  .company-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .detail-span-2 {
    grid-column: span 1;
  }

  .invoice-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .dialog-footer-actions {
    display: flex;
    width: 100%;
  }

  .dialog-footer-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
}

@media screen and (max-width: 480px) {
  .detail-layout {
    gap: 12px;
  }

  .invoice-row {
    padding: 8px 0;
  }
}

/* ===== 品牌化 UI 重设计 ===== */
.order-management-page {
  --brand-gradient: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
  --brand-blue: #0a7fd7;
  --brand-deep: #0649b8;
  --brand-teal: #03d2c5;
  --ink: #0f172a;
  --muted: #64748b;
  --line: #e4edf7;
  --surface: #ffffff;
}

.order-management-page .page-heading {
  position: relative;
  padding-left: 18px;
}

.order-management-page .page-heading::before {
  content: '';
  position: absolute;
  left: 0;
  top: 5px;
  bottom: 5px;
  width: 5px;
  border-radius: 999px;
  background: var(--brand-gradient);
}

.order-management-page .main-header h1 {
  color: var(--ink);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.order-management-page .subtitle {
  color: var(--muted);
}

.order-card {
  border-radius: 16px;
  border-color: var(--line);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.07);
  overflow: hidden;
}

.order-filter-row {
  background: linear-gradient(180deg, #fbfdff 0%, #f3f8fe 100%);
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
}

.order-management-page ::v-deep .keyword-input .el-input__inner,
.order-management-page ::v-deep .filter-select .el-input__inner {
  border-radius: 10px;
  border-color: #dbe7f5;
  background-color: #fff;
}

.order-management-page ::v-deep .keyword-input .el-input__inner:focus,
.order-management-page ::v-deep .filter-select .el-input__inner:focus {
  border-color: var(--brand-blue);
  box-shadow: 0 0 0 3px rgba(10, 127, 215, 0.12);
}

.search-btn {
  background: var(--brand-gradient);
  color: #fff;
  border: none;
  border-radius: 10px;
  box-shadow: 0 8px 18px rgba(10, 127, 215, 0.26);
}

.search-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.03);
  box-shadow: 0 10px 22px rgba(10, 127, 215, 0.32);
}

.order-management-page ::v-deep .el-table th {
  background: #eef4fc !important;
  color: #0a5aa8;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid #dbe7f5;
  padding: 12px 0;
}

.order-management-page ::v-deep .el-table td {
  border-bottom: 1px solid #eef3f9;
}

.order-management-page ::v-deep .el-table__body tr:hover > td {
  background: #f4f9ff !important;
}

.order-management-page ::v-deep .el-table__row--level-1 > td {
  background: #f7faff;
}

.order-no-cell {
  color: var(--brand-blue);
  font-weight: 600;
}

.company-cell {
  color: var(--ink);
  font-weight: 700;
}

.order-management-page ::v-deep .status-pill.el-tag {
  border: none;
  border-radius: 999px;
  padding: 4px 11px;
  font-weight: 700;
  line-height: 1.4;
}

.order-management-page ::v-deep .status-pill.el-tag--success {
  background: rgba(3, 210, 197, 0.13);
  color: #0b8a76;
}

.order-management-page ::v-deep .status-pill.el-tag--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}

.order-management-page ::v-deep .status-pill.el-tag--danger {
  background: rgba(225, 29, 72, 0.11);
  color: #be123c;
}

.order-management-page ::v-deep .storage-tag.el-tag {
  background: var(--brand-gradient);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 3px 10px;
  font-weight: 600;
  line-height: 1.4;
}

.action-buttons .btn {
  min-width: 92px;
  justify-content: center;
  padding: 8px 14px;
  font-size: 12px;
  border-radius: 9px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.action-buttons .action-view {
  background: #fff;
  color: var(--brand-blue);
  box-shadow: inset 0 0 0 1px rgba(10, 127, 215, 0.28);
}

.action-buttons .action-view:hover {
  background: #eaf4ff;
}

.action-buttons .action-sync {
  background: #f1f5f9;
  color: #475569;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.action-buttons .action-sync:hover {
  background: #e6edf6;
  color: #334155;
}

.action-buttons .action-invoice {
  background: #ecfdf9;
  color: #0b8a76;
  box-shadow: inset 0 0 0 1px rgba(3, 210, 197, 0.34);
}

.action-buttons .action-invoice:hover {
  background: #d7f7f0;
}

.pagination-wrap {
  border-top: 1px solid var(--line);
  background: var(--surface);
}

.detail-card {
  border-radius: 14px;
  border-color: var(--line);
  box-shadow: none;
}

.detail-card .card-header {
  background: #f7fafd;
}

.detail-item span {
  color: var(--muted);
  font-weight: 600;
}

.detail-item strong {
  color: var(--ink);
  font-weight: 700;
}

.invoice-row {
  border-color: #eef3f9;
}
</style>
